import {
  Block,
  ButtonIcon,
  CheckCircle,
  Divider,
  useStyletron,
} from "@mezo-org/mezo-clay"
import { Form, FormikErrors, FormikProps, withFormik } from "formik"

import { AmountInput } from "#/ui/Input"
import { StepperModalButton } from "#/ui/Modal"

export type AddLiquidityFormValue = {
  token1Amount: string
  token2Amount: string
}

type AddLiquidityFormBaseProps = {
  token1Balance: string
  token2Balance: string
}

export type OnSubmitAddLiquidityForm = (values: AddLiquidityFormValue) => void

type AddLiquidityFormProps = AddLiquidityFormBaseProps & {
  onSubmitForm: OnSubmitAddLiquidityForm
}

function AddLiquidityFormBase({
  handleSubmit,
  token1Balance,
  token2Balance,
  isValid,
  getFieldHelpers,
  getFieldMeta,
  getFieldProps,
}: AddLiquidityFormBaseProps & FormikProps<AddLiquidityFormValue>) {
  const [css, theme] = useStyletron()

  const token1FieldHelper = getFieldHelpers("token1Amount")
  const token1FieldMeta = getFieldMeta<string>("token1Amount")
  const toke1FieldProps = getFieldProps("token1Amount")

  const token2FieldHelper = getFieldHelpers("token2Amount")
  const token2FieldMeta = getFieldMeta<string>("token2Amount")
  const toke2FieldProps = getFieldProps("token2Amount")

  return (
    <Form
      className={css({
        marginTop: theme.sizing.scale900,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      })}
      onSubmit={handleSubmit}
    >
      <AmountInput
        label="Asset one"
        token="MUSD"
        balanceToken={token1Balance}
        balanceUsd="100,000"
        placeholder="0"
        {...toke1FieldProps}
        value={token1FieldMeta.value}
        error={Boolean(token1FieldMeta.error)}
        errorMessage={token1FieldMeta.error}
        onChange={(event) => {
          token1FieldHelper.setValue(event.target.value)
          // TODO: We should calculate the `token2` value based on the `token1`
          // value.
          token2FieldHelper.setValue(event.target.value, true)
        }}
        onButtonMax={
          token1Balance
            ? () => {
                token1FieldHelper.setValue(token1Balance)
              }
            : undefined
        }
      />
      <Block marginTop="scale800" marginBottom="scale800" position="relative">
        <ButtonIcon
          shape="circle"
          size="small"
          kind="secondary"
          overrides={{
            Root: {
              style: {
                position: "absolute",
                left: "0",
                right: "0",
                margin: "auto",
                top: "50%",
                transform: "translate(-50%, -50%)",
              },
            },
          }}
        >
          {/* TODO: Change icon */}
          <CheckCircle />
        </ButtonIcon>
        <Divider $size="section" />
      </Block>
      <AmountInput
        label="Asset two"
        token="BTC"
        balanceToken={token2Balance}
        balanceUsd="29,234"
        placeholder="0"
        {...toke2FieldProps}
        value={token2FieldMeta.value}
        error={Boolean(token2FieldMeta.error)}
        errorMessage={token2FieldMeta.error}
        onChange={(event) => token2FieldHelper.setValue(event.target.value)}
        onButtonMax={
          token1Balance
            ? () => {
                token2FieldHelper.setValue(token2Balance)
              }
            : undefined
        }
        isMaxDisabled
        // TODO: change the background color when the input is disabled.
        disabled
      />
      <StepperModalButton
        type="submit"
        disabled={!isValid}
        overrides={{
          Root: {
            style: {
              marginTop: "auto",
            },
          },
        }}
      >
        Continue
      </StepperModalButton>
    </Form>
  )
}

export default withFormik<AddLiquidityFormProps, AddLiquidityFormValue>({
  mapPropsToValues: () => ({
    token1Amount: "0",
    token2Amount: "0",
  }),
  validate: (
    { token1Amount, token2Amount },
    // { token1Balance, token2Balance },
  ) => {
    const errors: FormikErrors<AddLiquidityFormValue> = {}

    // TODO: Use the `validateTokenAmount` from #1539 PR.
    errors.token1Amount =
      !token1Amount || token1Amount === "0" ? "Required field" : undefined
    errors.token2Amount =
      !token2Amount || token2Amount === "0" ? "Required field" : undefined

    if (errors.token1Amount && errors.token2Amount) return errors

    return {}
  },
  handleSubmit: (values, { props }) => {
    props.onSubmitForm(values)
  },
  validateOnBlur: false,
})(AddLiquidityFormBase)
